
import React, { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat"; // load on demand
import { DatePicker } from "antd";
import locale from "antd/es/date-picker/locale/en_US";
import type { CountdownProps } from 'antd';
import { Col, Row, Statistic } from 'antd';
import { signUp } from "../../app/api";
import { AxiosError } from "axios";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';

dayjs.extend(customParseFormat);

const letters = [
    "А",
    "Б",
    "В",
    "Г",
    "Д",
    "Е",
    "Ё",
    "Ж",
    "З",
    "И",
    "Й",
    "К",
    "Л",
    "М",
    "Н",
    "О",
    "Ө",
    "П",
    "Р",
    "С",
    "Т",
    "У",
    "Ү",
    "Ф",
    "Х",
    "Ц",
    "Ч",
    "Ш",
    "Щ",
    "Ъ",
    "Ь",
    "Ы",
    "Э",
    "Ю",
    "Я",
];

export default function RegisterForm() {
    const { t } = useTranslation();
    const [agree, setAgree] = useState(false);
    const [preview, setPreview] = useState<null | string>(null);

    const [registerLoading, setRegisterLoading] = useState(false);
    const [userCreated, setUserCreated] = useState(false);
    const [serverError, setServerError] = useState('');

    const { Countdown } = Statistic;
    const deadline = Date.now() + 1000 * 60 * 5; // Dayjs is also OK

    const [userBio, setUserBio] = useState({
        lastName: "",
        firstName: "",
        register_letter_1: "А",
        register_letter_2: "А",
        register_number: "",
        gender: "эрэгтэй",
        birthDate: dayjs().format("YYYY-MM-DD"),
        address: "",
        school: "",
        grade: "",
        parentPhone: "",
        email: "",
        phone: "",
        password: "",
        password_confirmation: "",
        image: undefined,
    });

    const fieldsName = [
       { key: 'lastName', name: 'Овог' },
         { key: 'firstName', name: 'Нэр' },
         { key: 'registerNumber', name: 'Регистрийн дугаар' },
         {key: 'gender', name: 'Хүйс'},
            {key: 'birthDate', name: 'Төрсөн он, сар, өдөр'},
            {key: 'phone', name: 'Хэрэглэгчийн нэвтрэх утасны дугаар'},
            {key: 'email', name: 'Имэйл'},
            {key: 'parentPhone', name: 'Эцэг эхийн утасны дугаар'},
            {key: 'address', name: 'Гэрийн хаяг'},
            {key: 'school', name: 'Сургууль'},
            {key: 'grade', name: 'Анги'},
            {key: 'password', name: 'Нууц үг'},
            {key: 'password_confirmation', name: 'Нууц үгээ давтана уу'},
    ]

    const [errors, setErrors] = useState<string[]>([]);

    const handleRegister = useCallback(async () => {
        setUserCreated(false);
        setServerError('');

        setRegisterLoading(true);
        const curErrors: string[] = [];

        Object.entries(userBio).forEach(([key, value]) => {
            if(!value) {
                curErrors.push(key);
            }
        });
        if(!agree) {
            curErrors.push('agree');
        }
        if(userBio.register_number.length !== 8) {
            curErrors.push('register_number');
        }

        setErrors(curErrors);
        if(curErrors.length === 0) {
            const formData = new FormData();
            Object.entries(userBio).forEach(([key, value]) => {
                if(value) {
                    formData.append(key, value);
                }
            });
            let registerNumber = userBio.register_letter_1 + userBio.register_letter_2 + userBio.register_number;
            formData.append('registerNumber', registerNumber);

            try {
                const resp = await signUp(formData);
                if(resp.status == 200) {
                    setUserBio({
                        lastName: "",
                        firstName: "",
                        register_letter_1: "A",
                        register_letter_2: "A",
                        register_number: "",
                        gender: "эрэгтэй",
                        birthDate: dayjs().format("YYYY-MM-DD"),
                        address: "",
                        school: "",
                        grade: "",
                        parentPhone: "",
                        email: "",
                        phone: "",
                        password: "",
                        password_confirmation: "",
                        image: undefined,
                    });

                    setUserCreated(true);
                }
            } catch(err) {
                const {response} = (err as AxiosError);

                setServerError(`Алдаа: ${JSON.stringify(response?.data)}`);
            }
        }
        setRegisterLoading(false);
    }, [userBio, agree]);

    return (
        <>
            {/* Dark Hero Header */}
            <div className="relative overflow-hidden bg-[#0a1628] -mt-20">
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-primary-600/20 blur-[120px]" />
                    <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-warm-500/15 blur-[100px]" />
                </div>
                <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
                <div className="relative pt-40 pb-16 sm:pt-48 sm:pb-20">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
                        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-warm-300 backdrop-blur-sm">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-warm-400 animate-pulse" />
                            {t('register.badge')}
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight mb-4">
                            {t('register.title')}
                        </h1>
                        <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                            {t('register.description')} <strong className="text-warm-400">{t('register.descriptionHighlight')}</strong> {t('register.descriptionEnd')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Form Section */}
            <div className="bg-neutral-50 py-16">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="card p-8 md:p-12">
                    <form>
                    <div className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            <label className="block">
                                <span className="block text-sm font-bold text-neutral-900 mb-2">{t('register.lastName')}</span>
                                <input
                                    required
                                    value={userBio.lastName}
                                    type="text"
                                    className={`input ${
                                        errors.includes("lastName")
                                            ? 'border-danger-500 bg-danger-50'
                                            : ''
                                    }`}
                                    placeholder={t('register.lastNamePlaceholder')}
                                    onChange={(e) =>
                                        setUserBio({ ...userBio, lastName: e.target.value })
                                    }
                                />
                                {errors.includes("lastName") && (
                                    <p className="mt-2 text-danger-600 text-sm font-medium">
                                        {t('register.lastNameError')}
                                    </p>
                                )}
                            </label>
                            <label className="block">
                                <span className="block text-sm font-bold text-neutral-900 mb-2">{t('register.firstName')}</span>
                                <input
                                    required
                                    value={userBio.firstName}
                                    type="text"
                                    className={`input ${
                                        errors.includes("firstName")
                                            ? 'border-danger-500 bg-danger-50'
                                            : ''
                                    }`}
                                    placeholder={t('register.firstNamePlaceholder')}
                                    onChange={(e) =>
                                        setUserBio({ ...userBio, firstName: e.target.value })
                                    }
                                />
                                {errors.includes("firstName") && (
                                    <p className="mt-2 text-danger-600 text-sm font-medium">
                                        {t('register.firstNameError')}
                                    </p>
                                )}
                            </label>
                        </div>
                        <label className="block">
                            <span className="block text-sm font-bold text-neutral-900 mb-2">{t('register.registerNumber')}</span>
                            <div className="flex gap-3">
                                <select
                                    value={userBio.register_letter_1}
                                    onChange={(e) => {
                                        setUserBio({
                                            ...userBio,
                                            register_letter_1: e.target.value,
                                        });
                                    }}
                                    className="w-16 px-3 py-3 rounded-xl border border-neutral-200 bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all font-bold"
                                >
                                    {letters.map((l, index) => (
                                        <option value={l} key={index}>
                                            {l}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={userBio.register_letter_2}
                                    onChange={(e) => {
                                        setUserBio({
                                            ...userBio,
                                            register_letter_2: e.target.value,
                                        });
                                    }}
                                    className="w-16 px-3 py-3 rounded-xl border border-neutral-200 bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all font-bold"
                                >
                                    {letters.map((l, index) => (
                                        <option value={l} key={index}>
                                            {l}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    required
                                    value={userBio.register_number}
                                    type="tel"
                                    className={`input flex-1 ${
                                        ["register_letter_1", "register_letter_2", "register_number"].reduce((prev, v) => errors.includes(v) || prev, false)
                                            ? 'border-danger-500 bg-danger-50'
                                            : ''
                                    }`}
                                    placeholder="12345678"
                                    maxLength={8}
                                    minLength={8}
                                    onChange={(e) =>
                                        setUserBio({
                                            ...userBio,
                                            register_number: e.target.value
                                                .replace(/[^0-9.]/g, "")
                                                .replace(/(\..*)\./g, "$1"),
                                        })
                                    }
                                />
                            </div>
                            {["register_letter_1", "register_letter_2", "register_number"].reduce((prev, v) => errors.includes(v) || prev, false) && (
                                <p className="mt-2 text-danger-600 text-sm font-medium">
                                    {t('register.registerNumberError')}
                                </p>
                            )}
                            <div className="flex items-start gap-3 mt-4 p-4 bg-primary-50 rounded-xl border border-primary-200">
                                <input
                                    id="link-checkbox"
                                    type="checkbox"
                                    checked={agree}
                                    onChange={e => {setAgree(e.target.checked)}}
                                    className="mt-1 w-5 h-5 text-primary-600 bg-white border-neutral-200 rounded focus:ring-primary-500 focus:ring-2"
                                />
                                <label
                                    htmlFor="link-checkbox"
                                    className={`text-sm font-medium ${
                                        errors.includes("agree") ? 'text-danger-600' : 'text-neutral-700'
                                    }`}
                                >
                                    {t('register.agreeRegister')}
                                </label>
                            </div>
                            {errors.includes("agree") && (
                                <p className="mt-2 text-danger-600 text-sm font-medium">
                                    {t('register.agreeError')}
                                </p>
                            )}
                        </label>

                                {/* <label className="basis-1/2">
                  <span className="text-gray-700">*Төрсөн он, сар, өдөр</span>
                  <div className="flex gap-4 mb-4">
                    <input
                      required
                      value={userBio.birthYear}
                      type="tel"
                      className="mt-1 basis-3/5 block text-[#001447] w-full rounded-2xl border-[#001447]/20  disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none"
                      placeholder="Төрсөн он"
                      maxLength={4}
                      minLength={4}
                      onChange={(e) =>
                        setUserBio({
                          ...userBio,
                          birthYear: e.target.value
                            .replace(/[^0-9.]/g, "")
                            .replace(/(\..*)\./g, "$1"),
                        })
                      }

                    />
                    <input
                      required
                      value={userBio.birthMonth}
                      type="tel"
                      className="mt-1 basis-1/5 block text-[#001447] w-full rounded-2xl border-[#001447]/20  disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none"
                      placeholder="cар"
                      maxLength={2}
                      minLength={2}
                      onChange={(e) =>
                        setUserBio({
                          ...userBio,
                          birthMonth: e.target.value
                            .replace(/[^0-9.]/g, "")
                            .replace(/(\..*)\./g, "$1"),
                        })
                      }

                    />
                    <input
                      required
                      value={userBio.birthDay}
                      type="tel"
                      className="mt-1 basis-1/5 block text-[#001447] w-full rounded-2xl border-[#001447]/20  disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none"
                      placeholder="өдөр"
                      maxLength={2}
                      minLength={2}
                      onChange={(e) =>
                        setUserBio({
                          ...userBio,
                          birthDay: e.target.value
                            .replace(/[^0-9.]/g, "")
                            .replace(/(\..*)\./g, "$1"),
                        })
                      }

                    />
                  </div>
                  {errors.includes("birthDay") && (
                    <p className="mt-2 text-red-500 text-xs italic">
                      Төрсөн өдөр оруулна уу
                    </p>
                  )}
                  {errors.includes("birthDay-invalid") && (
                    <p className="mt-2 text-red-500 text-xs italic">
                      Төрсөн өдөр зөв оруулна уу
                    </p>
                  )}
                </label> */}
                        <div className="grid md:grid-cols-2 gap-6">
                            <label className="block">
                                <span className="block text-sm font-bold text-neutral-900 mb-2">{t('register.gender')}</span>
                                <select
                                    value={userBio.gender}
                                    className="w-full px-4 py-3 rounded-xl border border-neutral-200 bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                                    onChange={(e) => setUserBio({
                                        ...userBio,
                                        gender: e.target.value as string,
                                    })}
                                >
                                    {[{value: "эрэгтэй", label: t('register.male')}, {value: "эмэгтэй", label: t('register.female')}].map((gender) => (
                                        <option value={gender.value} key={gender.value}>
                                            {gender.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="block">
                                <span className="block text-sm font-bold text-neutral-900 mb-2">{t('register.birthDate')}</span>
                                <div className="mt-1">
                                    {userBio.birthDate && (
                                        <DatePicker
                                            locale={locale}
                                            defaultValue={dayjs(userBio.birthDate, "YYYY-MM-DD")}
                                            style={{
                                                borderRadius: 14,
                                                height: 48,
                                                borderColor: "#e5e5e5",
                                                backgroundColor: "#fff",
                                                width: '100%',
                                            }}
                                            className="w-full"
                                            onChange={(_, dateString) => setUserBio({
                                                ...userBio,
                                                birthDate: (dateString as string) ||  "",
                                            })}
                                        />
                                    )}
                                </div>
                            </label>
                        </div>

                        <label className="block">
                            <span className="block text-sm font-bold text-neutral-900 mb-2">{t('register.userPhone')}</span>
                            <input
                                value={userBio.phone}
                                type="tel"
                                className={`input ${
                                    errors.includes("phone")
                                        ? 'border-danger-500 bg-danger-50'
                                        : ''
                                }`}
                                placeholder="12345678"
                                maxLength={8}
                                minLength={8}
                                onChange={(e) =>
                                    setUserBio({
                                        ...userBio,
                                        phone: e.target.value
                                            .replace(/[^0-9.]/g, "")
                                            .replace(/(\..*)\./g, "$1"),
                                    })
                                }
                            />
                            {errors.includes("phone") && (
                                <p className="mt-2 text-danger-600 text-sm font-medium">
                                    {t('register.phoneError')}
                                </p>
                            )}
                        </label>

                        <label className="block">
                            <span className="block text-sm font-bold text-neutral-900 mb-2">{t('register.email')}</span>
                            <input
                                value={userBio.email}
                                type="email"
                                className={`input ${
                                    errors.includes("email")
                                        ? 'border-danger-500 bg-danger-50'
                                        : ''
                                }`}
                                placeholder="example@email.com"
                                maxLength={256}
                                onChange={(e) =>
                                    setUserBio({
                                        ...userBio,
                                        email: e.target.value
                                    })
                                }
                            />
                            {errors.includes("email") && (
                                <p className="mt-2 text-danger-600 text-sm font-medium">
                                    {t('register.emailError')}
                                </p>
                            )}
                        </label>

                        <label className="block">
                            <span className="block text-sm font-bold text-neutral-900 mb-2">{t('register.parentPhone')}</span>
                            <input
                                value={userBio.parentPhone}
                                type="tel"
                                className={`input ${
                                    errors.includes("parentPhone")
                                        ? 'border-danger-500 bg-danger-50'
                                        : ''
                                }`}
                                placeholder="12345678"
                                maxLength={8}
                                minLength={8}
                                onChange={(e) =>
                                    setUserBio({
                                        ...userBio,
                                        parentPhone: e.target.value
                                            .replace(/[^0-9.]/g, "")
                                            .replace(/(\..*)\./g, "$1"),
                                    })
                                }
                            />
                            {errors.includes("parentPhone") && (
                                <p className="mt-2 text-danger-600 text-sm font-medium">
                                    {t('register.phoneError')}
                                </p>
                            )}
                        </label>

                        <label className="block">
                            <span className="block text-sm font-bold text-neutral-900 mb-2">{t('register.address')}</span>
                            <input
                                value={userBio.address}
                                onChange={(e) =>
                                    setUserBio({
                                        ...userBio,
                                        address: e.target.value,
                                    })
                                }
                                type="text"
                                className={`input ${
                                    errors.includes("address")
                                        ? 'border-danger-500 bg-danger-50'
                                        : ''
                                }`}
                                placeholder={t('register.addressPlaceholder')}
                            />
                            {errors.includes("address") && (
                                <p className="mt-2 text-danger-600 text-sm font-medium">
                                    {t('register.addressError')}
                                </p>
                            )}
                        </label>

                        <div className="grid md:grid-cols-2 gap-6">
                            <label className="block">
                                <span className="block text-sm font-bold text-neutral-900 mb-2">{t('register.school')}</span>
                                <input
                                    value={userBio.school}
                                    onChange={(e) =>
                                        setUserBio({
                                            ...userBio,
                                            school: e.target.value,
                                        })
                                    }
                                    type="text"
                                    className={`input ${
                                        errors.includes("school")
                                            ? 'border-danger-500 bg-danger-50'
                                            : ''
                                    }`}
                                    placeholder={t('register.schoolPlaceholder')}
                                />
                                {errors.includes("school") && (
                                    <p className="mt-2 text-danger-600 text-sm font-medium">
                                        {t('register.schoolError')}
                                    </p>
                                )}
                            </label>
                            <label className="block">
                                <span className="block text-sm font-bold text-neutral-900 mb-2">{t('register.grade')}</span>
                                <input
                                    value={userBio.grade}
                                    onChange={(e) =>
                                        setUserBio({
                                            ...userBio,
                                            grade: e.target.value,
                                        })
                                    }
                                    type="text"
                                    className={`input ${
                                        errors.includes("grade")
                                            ? 'border-danger-500 bg-danger-50'
                                            : ''
                                    }`}
                                    placeholder={t('register.gradePlaceholder')}
                                />
                                {errors.includes("grade") && (
                                    <p className="mt-2 text-danger-600 text-sm font-medium">
                                        {t('register.gradeError')}
                                    </p>
                                )}
                            </label>
                        </div>

                        <label className="block">
                            <span className="block text-sm font-bold text-neutral-900 mb-2">{t('register.photo')}</span>
                            <div className="flex items-center justify-center w-full">
                                <label
                                    htmlFor="dropzone-file"
                                    className={`flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-2xl cursor-pointer transition-all ${
                                        errors.includes("image")
                                            ? 'border-danger-500 bg-danger-50'
                                            : 'border-primary-300 bg-primary-50 hover:border-primary-500 hover:bg-primary-100'
                                    }`}
                                >
                                    {!userBio.image ? (
                                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                            <div className="w-16 h-16 rounded-full bg-primary-600 flex items-center justify-center mb-4">
                                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                            </div>
                                            <p className="mb-2 text-sm font-semibold text-neutral-900">
                                                {t('register.photoUpload')} <span className="text-primary-600 underline">{t('register.photoSelect')}</span>
                                            </p>
                                            <p className="text-xs text-neutral-500">
                                                {t('register.photoFormat')}
                                            </p>
                                        </div>
                                    ) : (
                                        preview && (
                                            <div className="relative">
                                                <img
                                                    src={preview}
                                                    className="max-h-56 rounded-xl shadow-md"
                                                    alt="Preview"
                                                />
                                                <div className="absolute top-2 right-2 bg-white rounded-full p-2 shadow-md">
                                                    <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                                </div>
                                            </div>
                                        )
                                    )}
                                    <input
                                        id="dropzone-file"
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        value={''}
                                        onChange={async (e) => {
                                            if(e.target.files) {
                                                const img = e.target.files[0];
                                                setUserBio({
                                                    ...userBio,
                                                    // @ts-ignore
                                                    image: img,
                                                });

                                                const readURL = (file: File) => {
                                                    return new Promise((res, rej) => {
                                                        const reader = new FileReader();
                                                        reader.onload = e => {
                                                            if(e.target) {
                                                                res(e.target.result);
                                                            } else {
                                                                res('');
                                                            }
                                                        };
                                                        reader.onerror = e => rej(e);
                                                        reader.readAsDataURL(file);
                                                    });
                                                };
                                                const previewUrl = (await readURL(img) as string);
                                                setPreview(previewUrl);
                                            }
                                        }}
                                    />
                                </label>
                            </div>
                            {errors.includes("image") && (
                                <p className="mt-2 text-danger-600 text-sm font-medium">
                                    {t('register.photoError')}
                                </p>
                            )}
                        </label>

                        <div className="grid md:grid-cols-2 gap-6">
                            <label className="block">
                                <span className="block text-sm font-bold text-neutral-900 mb-2">{t('register.passwordLabel')}</span>
                                <input
                                    required
                                    value={userBio.password}
                                    type="password"
                                    className={`input text-center text-2xl tracking-widest font-bold ${
                                        errors.includes("password")
                                            ? 'border-danger-500 bg-danger-50'
                                            : ''
                                    }`}
                                    placeholder="****"
                                    maxLength={4}
                                    minLength={4}
                                    onChange={(e) =>
                                        setUserBio({
                                            ...userBio,
                                            password: e.target.value
                                                .replace(/[^0-9.]/g, "")
                                                .replace(/(\..*)\./g, "$1"),
                                        })
                                    }
                                />
                                {errors.includes("password") && (
                                    <p className="mt-2 text-danger-600 text-sm font-medium">
                                        {t('register.passwordError')}
                                    </p>
                                )}
                            </label>
                            <label className="block">
                                <span className="block text-sm font-bold text-neutral-900 mb-2">{t('register.confirmPassword')}</span>
                                <input
                                    required
                                    value={userBio.password_confirmation}
                                    type="password"
                                    className={`input text-center text-2xl tracking-widest font-bold ${
                                        errors.includes("password_confirmation")
                                            ? 'border-danger-500 bg-danger-50'
                                            : ''
                                    }`}
                                    placeholder="****"
                                    maxLength={4}
                                    minLength={4}
                                    onChange={(e) =>
                                        setUserBio({
                                            ...userBio,
                                            password_confirmation: e.target.value
                                                .replace(/[^0-9.]/g, "")
                                                .replace(/(\..*)\./g, "$1"),
                                        })
                                    }
                                />
                                {errors.includes("password_confirmation") && (
                                    <p className="mt-2 text-danger-600 text-sm font-medium">
                                        {t('register.confirmPasswordError')}
                                    </p>
                                )}
                            </label>
                        </div>
                    </div>

                    <div className="mt-8">
                        {userCreated ? (
                            <div
                                className="bg-primary-50 border border-primary-200 text-primary-700 px-6 py-4 rounded-2xl"
                                role="alert"
                            >
                                <div className="flex items-center gap-3">
                                    <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    <div>
                                        <p className="font-bold mb-1">{t('register.success')}</p>
                                        <Link to={'/login'} className="text-primary-700 hover:text-primary-800 underline font-semibold">
                                            {t('register.successLogin')}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ) : !registerLoading ? (
                            <div className="flex flex-1 items-center justify-center">
                                <button
                                    type="submit"
                                    onClick={(e) => {e.preventDefault(); handleRegister()}}
                                    className="px-12 py-4 text-lg rounded-full font-bold text-white bg-gradient-to-r from-primary-600 to-warm-500 shadow-lg shadow-primary-600/25 hover:shadow-xl hover:shadow-warm-500/30 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-4 focus:ring-warm-300 transition-all duration-200"
                                >
                                    {t('register.submitBtn')}
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-1 items-center justify-center">
                                <button
                                    disabled
                                    className="px-12 py-4 text-lg rounded-2xl bg-neutral-200 text-neutral-500 font-semibold cursor-not-allowed"
                                >
                                    <span className="inline-block animate-spin mr-2">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                    </span>
                                    {t('register.submitting')}
                                </button>
                            </div>
                        )}

                        {serverError && (
                            <div
                                className="bg-danger-50 border border-danger-200 text-danger-700 px-6 py-4 rounded-2xl mt-6"
                                role="alert"
                            >
                                <div className="flex items-center gap-3">
                                    <svg className="w-6 h-6 text-danger-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                                    <span className="font-semibold">{serverError}</span>
                                </div>
                            </div>
                        )}
                    </div>
                    </form>
                </div>
            </div>
            </div>
        </>
    );
}
